import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { ChevronLeftIcon, ChevronRightIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentPageId, selectCurrentSectionId, selectQueryString } from "store/selectors";
import { Link } from "utils/routing";
import { getNavigationValues, pageUrl } from "utils/urls";

const getPageNavValues = (
  pages: PageType[],
  currentSectionId?: string | null,
  currentPageId?: string | null,
  queryString?: string,
) => {
  if (!currentSectionId || !currentPageId)
    return { nextId: null, prevId: null, nextURL: null, prevURL: null };
  const pageIds = pages.map((page) => page.id);
  const { nextId, prevId } = getNavigationValues(pageIds, { currentId: currentPageId });

  const nextURL = nextId
    ? new URL(
        pageUrl(currentSectionId, nextId, { additionalParams: queryString }),
        window.location.href,
      )
    : undefined;
  const prevURL = prevId
    ? new URL(
        pageUrl(currentSectionId, prevId, { additionalParams: queryString }),
        window.location.href,
      )
    : undefined;

  return { nextId, prevId, nextURL, prevURL };
};

type PageNavigationButtonsProps = {
  className?: string;
  onChange?: (itemId: string, itemUrl: URL) => void;
  disabled?: boolean;
  pages?: PageType[];
};

export const PageNavigationButtons: FC<PageNavigationButtonsProps> = ({
  className,
  onChange,
  disabled = false,
  pages = [],
}) => {
  const queryString = useAppSelector(selectQueryString);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const pageId = useAppSelector(selectCurrentPageId);

  const { nextId, prevId, nextURL, prevURL } = getPageNavValues(
    pages,
    currentSectionId,
    pageId,
    queryString,
  );

  return (
    <ContentNavigationButtons
      className={className}
      disabled={disabled}
      nextId={nextId ?? undefined}
      nextURL={nextURL}
      prevId={prevId ?? undefined}
      prevURL={prevURL}
      reference="page"
      onChange={onChange}
    />
  );
};

type ContentNavigationButtonsProps = {
  className?: string;
  onChange?: (itemId: string, itemUrl?: URL | null) => void;
  disabled?: boolean;
  reference: "page" | "student" | "topic";
  nextId?: string | null;
  prevId?: string | null;
  nextURL?: URL | null;
  prevURL?: URL | null;
  eventPrefix?: string;
  kind?: "secondary" | "tertiary";
};

// TODO: Unify with materials/NavigationButtons.tsx
/*
 * This component is used to navigate between different content items. It works in conjunction
 * with the store and the useContentNavigation hook to keep track of the next and previous items.
 * Consumers must send the list of items to navigate between and optionally include a query param
 * to update when the current item changes.
 * Previously, this component supported multiple types of navigation such as terms, outcomes, sections,
 * but now it only supports pages, topics and students given design changes but is left extensible for future use.
 */
export const ContentNavigationButtons: FC<ContentNavigationButtonsProps> = ({
  nextId,
  prevId,
  nextURL,
  prevURL,
  reference,
  disabled,
  onChange,
  className,
  eventPrefix = "",
  kind = "secondary",
}) => {
  const isPrevButtonLink = !(disabled || onChange) && prevURL;
  const isNextButtonLink = !(disabled || onChange) && nextURL;

  return (
    <div className={twMerge("flex items-center", className)}>
      <SquareIconButton
        aria-label={t("common.previous")}
        as={isPrevButtonLink ? Link : undefined}
        className="!rounded-r-none rounded-l-xl"
        // Only pass 'disabled' if *not* using Link; Link does not accept 'disabled'
        {...(!isPrevButtonLink && { disabled: !prevId || disabled })}
        eventName={`${eventPrefix}Previous ${reference}`}
        kind={kind}
        size="s"
        title={t(`tooltip.previous.${reference}`)}
        to={isPrevButtonLink && prevURL ? prevURL.toString() : ""}
        onClick={
          disabled || !onChange ? undefined : () => prevId != null && onChange(prevId, prevURL)
        }
      >
        <ChevronLeftIcon />
      </SquareIconButton>

      <SquareIconButton
        aria-label={t("common.next")}
        as={isNextButtonLink ? Link : undefined}
        className="!rounded-l-none rounded-r-xl"
        // Only pass 'disabled' if *not* using Link; Link does not accept 'disabled'
        {...(!isNextButtonLink && { disabled: !nextId || disabled })}
        eventName={`${eventPrefix}Next ${reference}`}
        kind={kind}
        size="s"
        title={t(`tooltip.next.${reference}`)}
        to={isNextButtonLink && nextURL ? nextURL.toString() : ""}
        onClick={
          disabled || !onChange ? undefined : () => nextId != null && onChange(nextId, nextURL)
        }
      >
        <ChevronRightIcon />
      </SquareIconButton>
    </div>
  );
};
